import { VoxelAsset, VoxelData } from '../../voxelTypes';
import { PALETTE } from '../../voxelColor';
import { addBox, addMiniBox } from './CatalogUtils';

export function getRareCatalog(): VoxelAsset[] {
  const assets: VoxelAsset[] = [];

  // 1. Founder's Lantern
  (() => {
    const voxels: VoxelData[] = [];
    // Ornate gold and black fluted stone base
    addBox(voxels, -2, 0, -2, 4, 1, 4, PALETTE.STONE_DARK);
    addBox(voxels, -1, 1, -1, 2, 1, 2, PALETTE.LAMP_GOLD); // gold band
    // Slender fluted black iron post
    for (let y = 2; y <= 9; y++) {
      voxels.push({ x: 0, y, z: 0, color: PALETTE.STONE_DARK, scale: 0.8 });
    }
    // Crossbars holding small side lanterns
    addBox(voxels, -2, 8, -0.4, 4, 0.8, 0.8, PALETTE.STONE_DARK);
    // Double lanterns at side arms
    const lanterns = [-2, 1.2];
    lanterns.forEach(lx => {
      addBox(voxels, lx, 7, -0.5, 1, 0.5, 1, PALETTE.LAMP_GOLD); // gold bracket
      voxels.push({ x: lx + 0.5, y: 7.5, z: 0, color: 0xffd700, scale: 0.8, materialType: 'emissive' }); // lantern core
      addBox(voxels, lx, 8.2, -0.5, 1, 0.4, 1, PALETTE.STONE_DARK); // cap
    });
    // Main crown lantern on peak
    addBox(voxels, -1, 10, -1, 2, 0.5, 2, PALETTE.LAMP_GOLD);
    voxels.push({ x: 0, y: 10.5, z: 0, color: 0xffb05b, scale: 1.0, materialType: 'emissive' }); // glow
    addBox(voxels, -1.2, 11.5, -1.2, 2.4, 0.8, 2.4, PALETTE.STONE_DARK); // black peak hat
    assets.push({
      id: `rare_founders_lantern`,
      name: `Founder's Ornate Lantern`,
      category: 'prop',
      description: 'A legendary streetlamp forged in iron and detailed with burnished brass trim, casting a magnificent warm amber glow.',
      voxels,
      dimensions: { x: 5, y: 13, z: 3 }
    });
  })();

  // 2. Town Square Fountain
  (() => {
    const voxels: VoxelData[] = [];
    // Giant circular double-basin carved white marble structure
    // Lower pool rim
    for (let x = -5; x <= 5; x++) {
      for (let z = -5; z <= 5; z++) {
        const dist = Math.hypot(x, z);
        if (dist <= 5.2 && dist >= 4.0) {
          addBox(voxels, x, 0, z, 1, 1.8, 1, 0xeef2f7); // White marble
        } else if (dist < 4.0) {
          addBox(voxels, x, 0, z, 1, 0.6, 1, 0xeef2f7); // floor
          addBox(voxels, x, 0.6, z, 1, 0.8, 1, PALETTE.WATER_CYAN_DEEP, 'water'); // deep water
        }
      }
    }
    // Central fluted column
    addBox(voxels, -1.5, 1.5, -1.5, 3, 4.5, 3, 0xeef2f7);
    // Upper basin bowl (marble)
    for (let x = -3; x <= 3; x++) {
      for (let z = -3; z <= 3; z++) {
        const dist = Math.hypot(x, z);
        if (dist <= 3.2 && dist >= 1.8) {
          addBox(voxels, x, 5.0, z, 1, 1, 1, 0xeef2f7);
        } else if (dist < 1.8) {
          addBox(voxels, x, 5.0, z, 1, 0.4, 1, 0xeef2f7);
          addBox(voxels, x, 5.4, z, 1, 0.4, 1, PALETTE.WATER_CYAN_SHALLOW, 'water'); // water
        }
      }
    }
    // Golden central statue topper (mini-voxels!)
    addMiniBox(voxels, -1.0, 6.0, -1.0, 2, 4.5, 2, PALETTE.LAMP_GOLD); // golden angel core
    addMiniBox(voxels, -3.0, 7.0, -1.0, 2, 3, 1, PALETTE.LAMP_GOLD); // wing left
    addMiniBox(voxels, 1.0, 7.0, -1.0, 2, 3, 1, PALETTE.LAMP_GOLD); // wing right
    // Cascading sparkling streams
    for (let y = 1.0; y <= 5.0; y += 0.5) {
      voxels.push({ x: -2.3, y, z: -0.2, color: PALETTE.WATER_FOAM, scale: 0.5, materialType: 'water' });
      voxels.push({ x: 2.3, y, z: 0.2, color: PALETTE.WATER_FOAM, scale: 0.5, materialType: 'water' });
    }
    assets.push({
      id: `rare_square_fountain`,
      name: `Town Square Fountain`,
      category: 'prop',
      description: 'A colossal carved white marble fountain carrying a majestic golden angel status spraying shimmering water cascades.',
      voxels,
      dimensions: { x: 11, y: 10, z: 11 }
    });
  })();

  // 3. First Harvest Basket
  (() => {
    const voxels: VoxelData[] = [];
    // Horn-shaped cornucopia wicker basket (expanding loop)
    for (let x = -4; x <= 4; x++) {
      const dist = x + 4.5; // 0.5 to 8.5
      const r = 0.5 + dist * 0.35; // radius expands
      const cy = 1.2 + x * 0.2; // arches up slightly
      for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          const dy = y - cy;
          const distFromCenter = Math.hypot(dy, z);
          const isRim = distFromCenter <= r && distFromCenter >= r - 0.6;
          if (isRim) {
            voxels.push({ x, y, z, color: PALETTE.THATCH_DARK, scale: 0.9 });
          }
        }
      }
    }
    // Rich harvest spill in front (mini-voxels!)
    addMiniBox(voxels, 4.0, 1.0, -2.5, 4, 3, 5, PALETTE.PUMPKIN_ORANGE); // large pumpkin
    addMiniBox(voxels, 5.0, 2.5, -0.5, 1, 2, 1, PALETTE.PUMPKIN_STEM); // stem
    addMiniBox(voxels, 3.0, 1.0, -1.0, 2.5, 2, 2.5, PALETTE.FLOWER_RED); // red apples
    addMiniBox(voxels, 4.0, 1.0, 1.5, 2.5, 2, 2.5, PALETTE.WHEAT_GOLD); // golden wheat bundle
    assets.push({
      id: `rare_harvest_basket`,
      name: `First Harvest Basket`,
      category: 'prop',
      description: 'A traditional horn of plenty woven cornucopia basket overflowing with giant pumpkins and fresh farm produce.',
      voxels,
      dimensions: { x: 10, y: 5, z: 6 }
    });
  })();

  // 4. Moonlit Weather Vane
  (() => {
    const voxels: VoxelData[] = [];
    // Tall post in dark silver
    for (let y = 0; y <= 7; y++) {
      voxels.push({ x: 0, y, z: 0, color: 0x4f545a, scale: 0.4 });
    }
    // Directional spikes
    voxels.push({ x: -1.2, y: 4, z: 0, color: 0x4f545a, scale: 0.4 });
    voxels.push({ x: 1.2, y: 4, z: 0, color: 0x4f545a, scale: 0.4 });
    // Crescent Moon cutout (emissive silver light!)
    for (let x = -1.5; x <= 1.5; x += 0.5) {
      for (let y = 8.0; y <= 11.0; y += 0.5) {
        const dx = x / 1.5;
        const dy = (y - 9.5) / 1.5;
        const dist = dx*dx + dy*dy;
        const isMoon = dist <= 1.0 && dist >= 0.4 && x <= 0.4;
        if (isMoon) {
          voxels.push({ x, y, z: 0, color: 0xaaddff, scale: 0.5, materialType: 'emissive' });
        }
      }
    }
    // Howling wolf silhouette cutout beside moon (mini-voxels!)
    addMiniBox(voxels, 0.5, 11.0, 0, 3, 3, 1, 0x4f545a); // wolf body
    addMiniBox(voxels, 0.0, 12.5, 0, 1.5, 2, 1, 0x4f545a); // angled muzzle pointing up to sky
    assets.push({
      id: `rare_moonlit_vane`,
      name: `Moonlit Weather Vane`,
      category: 'prop',
      description: 'A beautiful gothic weather vane holding a glowing silver crescent moon and howling wolf crest.',
      voxels,
      dimensions: { x: 4, y: 11, z: 4 }
    });
  })();

  // 5. Golden Acorn Mailbox
  (() => {
    const voxels: VoxelData[] = [];
    // Cast gold and white marble mailbox
    // Marble column
    addBox(voxels, -1, 0, -1, 2, 5, 2, 0xf5f5f5);
    // Gold band joints
    addBox(voxels, -1.2, 1, -1.2, 2.4, 0.6, 2.4, PALETTE.LAMP_GOLD);
    addBox(voxels, -1.2, 4, -1.2, 2.4, 0.6, 2.4, PALETTE.LAMP_GOLD);
    // Golden acorn box body
    addBox(voxels, -1.5, 5.0, -1.5, 3, 3, 3, PALETTE.LAMP_GOLD);
    // Shining white emissive cap
    for (let x = -2; x <= 2; x++) {
      for (let z = -2; z <= 2; z++) {
        const distZ = Math.abs(z);
        const capY = 8.0 - distZ * 0.7;
        voxels.push({ x, y: capY, z, color: 0xffffff, scale: 0.5, materialType: 'emissive' });
      }
    }
    assets.push({
      id: `rare_gold_mailbox`,
      name: `Golden Acorn Mailbox`,
      category: 'prop',
      description: 'A legendary postbox gilded entirely in metallic gold on a fluted white marble pedestal.',
      voxels,
      dimensions: { x: 4, y: 10, z: 4 }
    });
  })();

  // 6. Community Quilt
  (() => {
    const voxels: VoxelData[] = [];
    // Standing wooden framing stand
    addBox(voxels, -4, 0, -1, 1, 11, 2, PALETTE.WOOD_OAK_DARK);
    addBox(voxels, 3, 0, -1, 1, 11, 2, PALETTE.WOOD_OAK_DARK);
    addBox(voxels, -3, 10, -0.5, 6, 1, 1, PALETTE.WOOD_OAK_DARK);
    addBox(voxels, -3, 1, -0.5, 6, 0.5, 1, PALETTE.WOOD_OAK_DARK);
    // Mosaic colorful tile quilt hanging on frame
    const patchColors = [PALETTE.FLOWER_RED, PALETTE.FLOWER_BLUE, PALETTE.FLOWER_YELLOW, 0x1d7070, 0x477a33, 0x993922, 0xffffff, 0x33363b];
    for (let x = -3; x <= 3; x++) {
      for (let y = 2; y <= 9; y++) {
        const col = patchColors[Math.abs(x * 3 + y * 7) % patchColors.length]!;
        addBox(voxels, x, y, -0.1, 1, 1, 0.2, col);
      }
    }
    assets.push({
      id: `rare_community_quilt`,
      name: `Community Quilt Screen`,
      category: 'prop',
      description: 'A giant standing timber exhibition frame carrying a heritage patchwork quilt of complex mosaic geometric tiles.',
      voxels,
      dimensions: { x: 8, y: 11, z: 2 }
    });
  })();

  // 7. Ghost Hunter Bell
  (() => {
    const voxels: VoxelData[] = [];
    // Ornate dark-cobble shepherd's stand/hook
    addBox(voxels, -2, 0, -2, 4, 1, 4, PALETTE.STONE_DARK); // stone base plate
    addBox(voxels, -1, 1, -1.8, 2, 8, 1, PALETTE.STONE_DARK); // back support post
    addBox(voxels, -1, 9, -1.8, 2, 1, 3, PALETTE.STONE_DARK); // overhead arch
    addBox(voxels, -0.5, 8.2, 0.2, 1, 1, 1, PALETTE.STONE_MEDIUM); // hanging attachment

    // Golden hook ring connection (mini-voxels)
    addMiniBox(voxels, -0.25, 7.5, -0.05, 1, 2, 1, PALETTE.LAMP_GOLD);

    // Ethereal translucent sea-glass bell dome (hollow, flared math)
    const seaGlassCol = 0x99f7d0; // Pale-green translucent sea-glass
    const glowCol = 0x5effaa;     // Ethereal pale-green phosphorescent glow

    for (let my = 8; my <= 14; my++) { // y = my * 0.5, i.e., 4.0 to 7.0
      const yVal = my * 0.5;
      let outerR = 0;
      let innerR = 0;
      if (my === 8) { outerR = 2.4; innerR = 1.7; }
      else if (my === 9) { outerR = 2.0; innerR = 1.4; }
      else if (my === 10) { outerR = 1.7; innerR = 1.1; }
      else if (my === 11) { outerR = 1.5; innerR = 0.9; }
      else if (my === 12) { outerR = 1.3; innerR = 0.7; }
      else if (my === 13) { outerR = 1.0; innerR = 0.5; }
      else if (my === 14) { outerR = 0.8; innerR = -1; } // solid top cap

      for (let mx = -5; mx <= 5; mx++) {
        for (let mz = -5; mz <= 5; mz++) {
          const px = mx * 0.5;
          const pz = 0.2 + mz * 0.5;
          const dist = Math.hypot(mx, mz);
          if (dist <= outerR && dist >= innerR) {
            voxels.push({
              x: px,
              y: yVal,
              z: pz,
              color: seaGlassCol,
              scale: 0.5,
              materialType: 'glass'
            });
          }
        }
      }
    }

    // Clapper rod hanging inside the hollow dome
    for (let my = 9; my <= 12; my++) {
      voxels.push({
        x: 0,
        y: my * 0.5,
        z: 0.2,
        color: 0xa2f9d5,
        scale: 0.5,
        materialType: 'glass'
      });
    }

    // Glowing clapper ringer ball emitting pale-green phosphorescent light
    voxels.push({ x: 0, y: 4.0, z: 0.2, color: glowCol, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: -0.25, y: 3.75, z: 0.2, color: glowCol, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0.25, y: 3.75, z: 0.2, color: glowCol, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0, y: 3.75, z: -0.05, color: glowCol, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0, y: 3.75, z: 0.45, color: glowCol, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0, y: 3.5, z: 0.2, color: glowCol, scale: 0.5, materialType: 'emissive' });

    // Magical rising phosphorescent dust particles
    voxels.push({ x: -0.8, y: 4.5, z: -0.5, color: glowCol, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: 1.0, y: 5.5, z: 0.8, color: glowCol, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: -0.5, y: 6.8, z: 1.0, color: glowCol, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: 0.6, y: 7.8, z: -0.3, color: glowCol, scale: 0.3, materialType: 'emissive' });

    assets.push({
      id: `rare_ghost_bell`,
      name: `Ghost Hunter Bell`,
      category: 'prop',
      description: 'A magical calling bell crafted of translucent sea-glass, emitting an ethereal pale-green phosphorescent glow.',
      voxels,
      dimensions: { x: 4, y: 10, z: 4 }
    });
  })();

  // 8. Storyteller's Chair
  (() => {
    const voxels: VoxelData[] = [];
    // Oversized plush gold & crimson throne
    // Golden lion claws legs
    addBox(voxels, -2.5, 0, -2.5, 1, 1.5, 1, PALETTE.LAMP_GOLD);
    addBox(voxels, 1.5, 0, -2.5, 1, 1.5, 1, PALETTE.LAMP_GOLD);
    addBox(voxels, -2.5, 0, 1.5, 1, 1.5, 1, PALETTE.LAMP_GOLD);
    addBox(voxels, 1.5, 0, 1.5, 1, 1.5, 1, PALETTE.LAMP_GOLD);
    // Deep crimson velvet seat pad
    addBox(voxels, -2.5, 1.5, -2.5, 5, 2.0, 5, PALETTE.FLOWER_RED);
    // Carved gold scroll armrests
    addBox(voxels, -3, 3.5, -2, 1, 1.5, 4.5, PALETTE.LAMP_GOLD);
    addBox(voxels, 2, 3.5, -2, 1, 1.5, 4.5, PALETTE.LAMP_GOLD);
    // Massive wingback padded backrest
    addBox(voxels, -3, 3.5, -2.5, 6, 8.0, 1.2, PALETTE.FLOWER_RED); // velvet cushioning
    addBox(voxels, -3.2, 3.5, -2.8, 6.4, 8.5, 0.6, PALETTE.LAMP_GOLD); // golden carving back shell
    assets.push({
      id: `rare_story_chair`,
      name: `Storyteller's Velvet Throne`,
      category: 'prop',
      description: 'A majestic oversized storyteller wingback throne carved in gold filigree and padded in plush ruby-red velvet cushion.',
      voxels,
      dimensions: { x: 6.5, y: 12, z: 5.5 }
    });
  })();

  // 9. Winter Hearth
  (() => {
    const voxels: VoxelData[] = [];
    // Tall cobblestone fireplace hearth
    addBox(voxels, -4, 0, -2.5, 8, 1, 5, PALETTE.COBBLE); // stone floor hearth slab
    // Heavy side chimney pillars
    addBox(voxels, -4, 1, -2, 2.2, 10, 4, PALETTE.COBBLE);
    addBox(voxels, 1.8, 1, -2, 2.2, 10, 4, PALETTE.COBBLE);
    // Firebox opening backing
    addBox(voxels, -1.8, 1, -2, 3.6, 5, 1, PALETTE.STONE_DARK);
    // Cozy mantel shelf (dark cedar)
    addBox(voxels, -4.5, 6.5, -2.2, 9, 0.8, 4.4, PALETTE.WOOD_OAK_DARK);
    // Glowing crackling fire logs inside fireplace (emissive red/orange)
    addBox(voxels, -1.5, 1, -1.5, 3, 1, 2, PALETTE.WOOD_PINE_BARK); // fuel wood
    addBox(voxels, -1.2, 1.5, -1.2, 2.4, 1.2, 1.5, PALETTE.FIRE_ORANGE, 'emissive'); // glowing red coals
    addMiniBox(voxels, -1.5, 2.0, -1.5, 3, 4, 1, PALETTE.FLOWER_YELLOW, 'emissive'); // flickering golden fire tongues
    assets.push({
      id: `rare_winter_hearth`,
      name: `Winter Fireplace Hearth`,
      category: 'prop',
      description: 'A magnificent rustic fieldstone chimney hearth containing a crackling wood bonfire lit with flickering fire embers.',
      voxels,
      dimensions: { x: 9, y: 11, z: 5 }
    });
  })();

  // 10. Spring Festival Arch
  (() => {
    const voxels: VoxelData[] = [];
    // Double whitewashed wooden columns
    for (let y = 0; y <= 9; y++) {
      voxels.push({ x: -4, y, z: -1, color: 0xfbfbf9, scale: 0.95 });
      voxels.push({ x: 3, y, z: -1, color: 0xfbfbf9, scale: 0.95 });
      voxels.push({ x: -4, y, z: 1, color: 0xfbfbf9, scale: 0.95 });
      voxels.push({ x: 3, y, z: 1, color: 0xfbfbf9, scale: 0.95 });
    }
    // Arched cross gables (white timber)
    addBox(voxels, -4.5, 10, -1, 9, 0.8, 2, 0xfbfbf9);
    // Beautiful massive cloud of soft pink cherry blossoms draping the arch (foliage)
    // pink colors: 0xffb5c5, 0xff99b0, 0xff7a9a
    for (let x = -5; x <= 4; x++) {
      for (let y = 8; y <= 12; y++) {
        for (let z = -2; z <= 2; z++) {
          const distFromArchCenter = Math.hypot(x + 0.5, y - 10.5);
          if (distFromArchCenter <= 2.2) {
            const isFringe = distFromArchCenter > 1.4;
            const color = isFringe ? 0xffb5c5 : 0xff7a9a;
            voxels.push({ x, y, z, color, scale: 0.9, materialType: 'foliage' });
          }
        }
      }
    }
    assets.push({
      id: `rare_spring_arch`,
      name: `Spring Cherry Arch`,
      category: 'prop',
      description: 'A festival ceremonial archway constructed of whitewashed wood and blanketed in a fluffy cloud of sweet pink cherry blossoms.',
      voxels,
      dimensions: { x: 10, y: 13, z: 5 }
    });
  })();

  // 11. Summer Firefly Jar
  (() => {
    const voxels: VoxelData[] = [];
    // Rustic weathered mossy stump base
    addBox(voxels, -2, 0, -2, 4, 3, 4, PALETTE.WOOD_PINE_BARK);
    addBox(voxels, -2, 0, -2, 1, 3, 1, PALETTE.MOSS); // moss creeping
    // Giant glass preservation jar body (glass material)
    for (let y = 3.0; y <= 6.5; y += 0.5) {
      for (let x = -1.5; x <= 1.5; x += 0.5) {
        for (let z = -1.5; z <= 1.5; z += 0.5) {
          const distSq = x*x + z*z;
          if (distSq <= 2.0 && distSq >= 0.8) {
            voxels.push({ x, y, z, color: PALETTE.GLASS_WINDOW, scale: 0.5, materialType: 'glass' });
          }
        }
      }
    }
    // Copper jar lid on top
    addBox(voxels, -1, 7, -1, 2, 1, 2, PALETTE.LAMP_GOLD);
    // Glowing magical green fireflies floating inside the jar (emissive neon green!)
    voxels.push({ x: -0.4, y: 3.5, z: 0, color: 0x39ff14, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: 0.4, y: 4.5, z: -0.4, color: 0x39ff14, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: 0, y: 5.5, z: 0.3, color: 0x39ff14, scale: 0.3, materialType: 'emissive' });
    assets.push({
      id: `rare_firefly_jar`,
      name: `Summer Firefly Jar`,
      category: 'prop',
      description: 'An oversized glass apothecary jar sitting on a mossy wood stump, filled with glowing green magical forest fireflies.',
      voxels,
      dimensions: { x: 4, y: 8.5, z: 4 }
    });
  })();

  // 12. Autumn Leaf Cart
  (() => {
    const voxels: VoxelData[] = [];
    // Timber wagon barrow bed
    addBox(voxels, -3, 1.2, -2, 6, 0.5, 4, PALETTE.WOOD_OAK_MEDIUM);
    // Side panels
    addBox(voxels, -3, 1.7, -2, 6, 1.2, 0.5, PALETTE.WOOD_OAK_DARK);
    addBox(voxels, -3, 1.7, 1.5, 6, 1.2, 0.5, PALETTE.WOOD_OAK_DARK);
    addBox(voxels, -3, 1.7, -1.5, 0.5, 1.2, 3, PALETTE.WOOD_OAK_DARK);
    // Wooden axle and spoked wagon wheels (mini-voxels!)
    for (let y = 0; y <= 3; y++) {
      voxels.push({ x: -1.5, y, z: -2.3, color: PALETTE.WOOD_OAK_DARK, scale: 0.9 });
      voxels.push({ x: -1.5, y, z: 2.3, color: PALETTE.WOOD_OAK_DARK, scale: 0.9 });
    }
    // Pull bars
    addBox(voxels, 3, 1.5, -1.2, 3, 0.5, 0.5, PALETTE.WOOD_OAK_MEDIUM);
    addBox(voxels, 3, 1.5, 0.7, 3, 0.5, 0.5, PALETTE.WOOD_OAK_MEDIUM);
    // Massive fluffy pile of glowing orange and crimson maple leaves overflowing the cart (foliage)
    // orange: 0xd69622, red: 0xb33a1e
    for (let x = -2.5; x <= 2.5; x += 0.5) {
      for (let y = 2.2; y <= 5.5; y += 0.5) {
        for (let z = -1.5; z <= 1.5; z += 0.5) {
          const dy = y - 2.2;
          const rLimit = 2.0 - dy * 0.45;
          if (Math.hypot(x, z) <= rLimit) {
            const isRed = (x + y + z) % 2 === 0;
            const color = isRed ? PALETTE.LEAF_AUTUMN_RED : PALETTE.LEAF_AUTUMN_GOLD;
            voxels.push({ x, y, z, color, scale: 0.5, materialType: 'foliage' });
          }
        }
      }
    }
    assets.push({
      id: `rare_autumn_cart`,
      name: `Autumn Harvest Leaf Cart`,
      category: 'prop',
      description: 'A cozy timber harvest pushcart loaded with a massive overflowing hill of glowing golden-amber and ruby-red autumn leaves.',
      voxels,
      dimensions: { x: 7, y: 6, z: 5.5 }
    });
  })();

  return assets;
}
